import {
  ConferenceCreatedByOtherEvent,
  GroupStatusEvent,
  MessageMarkedAsRead,
  useIM,
  HistoryMessageItem,
  MessageCallReceived,
  UnMarkedAsRead,
} from '@wui/im'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'


import { chatModel } from '@/entities/messages'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import {
  ChatMark,
  ReadMarksByMessageEventId,
  ReadMarksByMessageEventIdIndexes,
  ReadTimestampByMessageEventId,
  ChatMarkTimestamp,
} from '@/shared/types/store'

export type MessageViewer = {
  contactId: string
  timestamp?: number
}

export const useStoreLastReadEventIds = defineStore('lastReadEventIds', () => {
  const lastMessageEventIdByRoomAndContactId = ref<ReadMarksByMessageEventIdIndexes>({})
  const lastMessageRankByRoomAndContactId = ref<ReadMarksByMessageEventIdIndexes>({})
  const readMarksByMessageEventId = ref<ReadMarksByMessageEventId>({})
  const messagesMarkedUnread = ref(new Map<string, UnMarkedAsRead>())
  const readTimestampsByMessageEventId = ref<ReadTimestampByMessageEventId>({})
  const { currentUser } = useCurrentUser()
  const { currentGuest } = useGuestAuth()

  const currentUserIMLogin = computed(() => currentUser.value?.imLogin ?? currentGuest.value?.pServed)
  const getByMessageEventId = (messageEventId: number): ChatMark[] => {
    return readMarksByMessageEventId.value[messageEventId] ?? []
  }

  const getViewersByMessageEventId = (pServed: string, messageEventId: number): MessageViewer[] => {
    const roomMarks = lastMessageEventIdByRoomAndContactId.value[pServed]
    if (!roomMarks || !messageEventId) return []

    return Object.entries(roomMarks)
      .filter(([contactId, lastReadEventId]) => (
        lastReadEventId >= messageEventId
        && contactId !== currentUserIMLogin.value
      ))
      .map(([contactId, lastReadEventId]) => {
        const timestamps = readTimestampsByMessageEventId.value[lastReadEventId] ?? []
        const mark = timestamps.find((item) => item.contactId === contactId)

        return {
          contactId,
          ...(mark?.timestamp !== undefined ? { timestamp: mark.timestamp } : {}),
        }
      })
      .sort((a, b) => (a.timestamp ?? Number.POSITIVE_INFINITY) - (b.timestamp ?? Number.POSITIVE_INFINITY))
  }

  const getTimestampsByMessageEventId = (messageEventId: number): ChatMarkTimestamp[] => {
    return readTimestampsByMessageEventId.value[messageEventId] ?? []
  }

  const getMessageEventIdByRoomAndContactId = (roomId: string, contactId: string): number => {
    return lastMessageEventIdByRoomAndContactId.value[roomId]?.[contactId] ?? 0
  }

  const getOwnMessageRankByRoomAndContactId = (pServed: string): number => {
    if (currentUserIMLogin.value) {
      return lastMessageRankByRoomAndContactId.value[pServed]?.[currentUserIMLogin.value] ?? 0
    }
    return 0
  }

  const getHasUnreadMessageBeforeCall = (callEvent: HistoryMessageItem<MessageCallReceived>): boolean => {
    const { findRoomMessages } = chatModel.useChatMessageStore()

    const lastReadMessageRank = getOwnMessageRankByRoomAndContactId(callEvent.item.pServed)
    const messages = findRoomMessages(callEvent.item.pServed)

    return !!messages.find(event => event.item.type !== 'MessageCallReceived' && event.rank < callEvent.rank && event.rank > lastReadMessageRank)
  }

  const getLastMessageEvents = (roomId: string) => lastMessageEventIdByRoomAndContactId.value[roomId]

  const getMarkedUnread = (pServed: string) => messagesMarkedUnread.value.get(pServed)

  const updateLastReadEventId = (
    pServedUser: string,
    contactId: string,
    messageEventId: number,
    rank: number,
    lastReadTimestamp?: number,
  ) => {
    if (!lastMessageEventIdByRoomAndContactId.value[pServedUser]) {
      lastMessageEventIdByRoomAndContactId.value[pServedUser] = {}
    }
    if (!lastMessageRankByRoomAndContactId.value[pServedUser]) {
      lastMessageRankByRoomAndContactId.value[pServedUser] = {}
      if (currentUserIMLogin.value) {
        lastMessageRankByRoomAndContactId.value[pServedUser][currentUserIMLogin.value] = 0
      }
    }
    const prevEventId = lastMessageEventIdByRoomAndContactId.value[pServedUser][contactId]
    const prevRank = lastMessageRankByRoomAndContactId.value[pServedUser][contactId] ?? 0

    if (messageEventId && !readMarksByMessageEventId.value[messageEventId]) {
      readMarksByMessageEventId.value[messageEventId] = []
    }

    if (prevEventId && prevEventId < messageEventId) {
      const oldIndex = readMarksByMessageEventId.value[prevEventId]!.findIndex(x => x.contactId === contactId)
      if (oldIndex > -1) {
        readMarksByMessageEventId.value[prevEventId]!.splice(oldIndex, 1)
      }
    }

    if (rank > 0 && rank > prevRank) {
      lastMessageRankByRoomAndContactId.value[pServedUser][contactId] = rank
    }

    if ((messageEventId > 0 && !prevEventId) || (prevEventId && prevEventId < messageEventId)) {
      readMarksByMessageEventId.value[messageEventId]!.push({
        contactId,
        eventId: messageEventId,
      })
      lastMessageEventIdByRoomAndContactId.value[pServedUser][contactId] = messageEventId
    }

    if (lastReadTimestamp) {
      if (messageEventId && !readTimestampsByMessageEventId.value[messageEventId]) {
        readTimestampsByMessageEventId.value[messageEventId] = []
      }

      const index = readTimestampsByMessageEventId.value[messageEventId]!.findIndex((item: ChatMarkTimestamp) => item.contactId === contactId)
      if (index > -1) {
        readTimestampsByMessageEventId.value[messageEventId]!.splice(index, 1)
      }

      readTimestampsByMessageEventId.value[messageEventId]!.push({
        contactId,
        timestamp: lastReadTimestamp,
      })
    }
  }

  const updateFromContactStatus = (event: GroupStatusEvent) => {
    const { pServed, myLastReadEventId, extendedParams: { lastReadRank, ltsmar, lrei, lrer, contacts = [], unMarkedAsRead }} = event

    const applyReadMark = (contactId: string, targetEventId: number, targetRank: number, timestamp?: number) => {
      const { eventId, rank } = resolveReadMarkTarget(pServed, targetEventId, targetRank)
      resetReadMarkIfDowngrade(pServed, contactId, eventId)
      updateLastReadEventId(pServed, contactId, eventId, rank, timestamp)

      const deletedEvent = chatModel.useChatMessageStore().getDeletedEvent(targetEventId)
      if (deletedEvent) {
        handleDeletedMessage(pServed, deletedEvent.eventId)
      }
    }

    if (myLastReadEventId && currentUserIMLogin.value && lastReadRank) {
      applyReadMark(currentUserIMLogin.value, myLastReadEventId, lastReadRank, ltsmar)
    }
    if (lrei && lrer !== undefined) {
      applyReadMark(pServed, lrei, lrer, ltsmar)
    }
    contacts.filter(({ lrei }) => !!lrei).forEach(({ lrei, id, lrer, ltsmar }) => {
      applyReadMark(id, lrei, lrer, ltsmar)
    })

    if (unMarkedAsRead) {
      messagesMarkedUnread.value.set(pServed, unMarkedAsRead)
    } else if (messagesMarkedUnread.value.has(pServed)) {
      messagesMarkedUnread.value.delete(pServed)
    }
  }

  const resolveReadMarkTarget = (pServed: string, eventId: number, rank: number): { eventId: number, rank: number } => {
    const chatStore = chatModel.useChatMessageStore()
    const visibleMessage = chatStore.findLastReadableMessageAtOrBefore(pServed, eventId)
    const lastMessage = chatStore.findLastMessage(pServed)
    const lastMessageRank = lastMessage && lastMessage.eventId <= eventId
      ? (lastMessage.rank ?? 0)
      : 0

    if (visibleMessage) {
      return {
        eventId: visibleMessage.eventId,
        rank: Math.max(visibleMessage.rank ?? 0, lastMessageRank, rank),
      }
    }

    // mtMarkAsReadEventGroup: rank 0 и нет Incoming/Outgoing в сторе —
    // берём rank последнего события комнаты, если оно покрыто отметкой
    if (lastMessage && lastMessageRank > 0) {
      return {
        eventId: lastMessage.eventId,
        rank: Math.max(lastMessageRank, rank),
      }
    }

    return { eventId, rank }
  }

  const resetReadMarkIfDowngrade = (pServed: string, contactId: string, resolvedEventId: number) => {
    const roomMarks = lastMessageEventIdByRoomAndContactId.value[pServed]
    const prevEventId = roomMarks?.[contactId]

    if (!prevEventId || prevEventId <= resolvedEventId) {
      return
    }

    const orphanMarks = readMarksByMessageEventId.value[prevEventId]
    if (orphanMarks) {
      const markIndex = orphanMarks.findIndex((mark) => mark.contactId === contactId)
      if (markIndex > -1) {
        orphanMarks.splice(markIndex, 1)
      }
      if (orphanMarks.length === 0) {
        delete readMarksByMessageEventId.value[prevEventId]
      }
    }

    const roomRanks = lastMessageRankByRoomAndContactId.value[pServed] ?? {}
    lastMessageRankByRoomAndContactId.value[pServed] = roomRanks
    roomMarks![contactId] = 0
    roomRanks[contactId] = 0
  }

  const isReadMarkOnVisibleMessage = (pServed: string, eventId: number): boolean => {
    const visibleMessage = chatModel.useChatMessageStore().findLastReadableMessageAtOrBefore(pServed, eventId)
    return visibleMessage?.eventId === eventId
  }

  const reconcileReadMarksForRoom = (pServed: string) => {
    const roomMarks = lastMessageEventIdByRoomAndContactId.value[pServed]
    if (!roomMarks) {
      return
    }

    const roomRanks = lastMessageRankByRoomAndContactId.value[pServed] ?? {}

    Object.entries(roomMarks).forEach(([contactId, eventId]) => {
      if (!eventId) {
        return
      }

      if (isReadMarkOnVisibleMessage(pServed, eventId)) {
        return
      }

      const { eventId: resolvedEventId, rank } = resolveReadMarkTarget(pServed, eventId, roomRanks[contactId] ?? 0)
      if (resolvedEventId === eventId) {
        return
      }

      resetReadMarkIfDowngrade(pServed, contactId, resolvedEventId)
      updateLastReadEventId(pServed, contactId, resolvedEventId, rank)
    })
  }

  const handleDeletedMessage = (roomPServed: string, eventId: number) => {
    const marks = getByMessageEventId(eventId)
    if (!marks?.length) {
      return
    }

    const targetMessage = chatModel.useChatMessageStore().findLastReadableMessageAtOrBefore(roomPServed, eventId)

    if (!targetMessage) {
      delete readMarksByMessageEventId.value[eventId]
      marks.forEach((mark) => {
        const roomMarks = lastMessageEventIdByRoomAndContactId.value[roomPServed]
        if (roomMarks?.[mark.contactId] === eventId) {
          delete roomMarks[mark.contactId]
        }
      })
      return
    }

    if (targetMessage.eventId === eventId) {
      return
    }

    if (!lastMessageEventIdByRoomAndContactId.value[roomPServed]) {
      lastMessageEventIdByRoomAndContactId.value[roomPServed] = {}
    }
    if (!lastMessageRankByRoomAndContactId.value[roomPServed]) {
      lastMessageRankByRoomAndContactId.value[roomPServed] = {}
    }

    if (!readMarksByMessageEventId.value[targetMessage.eventId]) {
      readMarksByMessageEventId.value[targetMessage.eventId] = []
    }

    marks.forEach(mark => {
      lastMessageEventIdByRoomAndContactId.value[roomPServed]![mark.contactId] = targetMessage.eventId
      lastMessageRankByRoomAndContactId.value[roomPServed]![mark.contactId] = targetMessage.rank ?? 0

      const marksByEventId = readMarksByMessageEventId.value[targetMessage.eventId]
      if (marksByEventId && !!marksByEventId.find(m => m.contactId === mark.contactId)) {
        return
      }

      marksByEventId!.push({
        contactId: mark.contactId,
        eventId: targetMessage.eventId,
      })
    })

    delete readMarksByMessageEventId.value[eventId]
  }

  const { onConferenceCreated, onConferenceUpdated, onMarkAsRead } = useIM()

  onConferenceCreated((event: ConferenceCreatedByOtherEvent) => {
    if (currentUserIMLogin.value) {
      const { pServed, eventId, rank } = event
      updateLastReadEventId(pServed, currentUserIMLogin.value, eventId, rank)
    }
  })

  onMarkAsRead((event: MessageMarkedAsRead) => {
    // mtMarkAsReadEventGroup часто приходит с rank: 0 — unread считается по rank,
    // поэтому восстанавливаем его из локальной истории по eventIdMarkedAsRead
    const { eventId, rank } = resolveReadMarkTarget(
      event.pServed,
      event.eventIdMarkedAsRead,
      event.rank,
    )
    resetReadMarkIfDowngrade(event.pServed, event.executor, eventId)
    updateLastReadEventId(
      event.pServed,
      event.executor,
      eventId,
      rank,
      event.timestamp,
    )
  })

  onConferenceUpdated(updateFromContactStatus)

  return {
    getByMessageEventId,
    getViewersByMessageEventId,
    getMessageEventIdByRoomAndContactId,
    getOwnMessageRankByRoomAndContactId,
    getLastMessageEvents,
    lastMessageEventIdByRoomAndContactId,
    lastMessageRankByRoomAndContactId,
    readMarksByMessageEventId,
    readTimestampsByMessageEventId,
    getTimestampsByMessageEventId,
    updateFromContactStatus,
    updateLastReadEventId,
    getHasUnreadMessageBeforeCall,
    getMarkedUnread,
    handleDeletedMessage,
    reconcileReadMarksForRoom,
  }
})
